import React, { useState, useRef, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Divider,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Card,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  TextField,
} from "@mui/material";
import { FaTimes } from "react-icons/fa";
import EventCard from "../../components/eventcard";
import SpecialNotes from "../../components/notes";
import { UPDATE_ORDER_URL } from "../../utils";

const OrderDetailsDialog = ({ open, onClose, selectedOrder }) => {
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const printRef = useRef();
  const details = selectedOrder ? JSON.parse(selectedOrder.details) : [];
  const events = selectedOrder ? JSON.parse(selectedOrder.events) : [];
  const [status, setStatus] = useState(
    selectedOrder ? selectedOrder?.status : ""
  );
  const [paymentStatus, setPaymentStatus] = useState(
    selectedOrder ? selectedOrder?.payment_status : ""
  );
  const [amount, setAmount] = useState(0);
  const [advAmount, setAdvAmount] = useState(0);

  useEffect(() => {
    if (status === "Rejected") {
      setPaymentStatus("Rejected");
      setAmount("");
    } else {
      setPaymentStatus("");
    }
  }, [status]);

  const handleSave = async () => {
    const payload = {
      status,
      payment_status: paymentStatus,
      amount_received: advAmount || 0,
      order_amount: amount || 0,
    };
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${UPDATE_ORDER_URL}?id=${selectedOrder?.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error("Failed to update status");
      onClose("e", true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography
          variant="h5"
          style={{ fontWeight: "bold", color: "#e94b4b" }}
        >
          Order Details
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", top: "10px", right: "10px" }}
        >
          <FaTimes />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers style={{ padding: "1.5rem" }}>
        <div ref={printRef}>
          {selectedOrder && (
            <>
              {/* Order Side Information */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}
              >
                {selectedOrder.side === "G"
                  ? "Groom's Side"
                  : selectedOrder.side === "B"
                  ? "Bride's Side"
                  : "Other"}
              </Typography>

              {/* Video Section */}
              {details && (
                <Card variant="outlined" style={{ marginBottom: "1.5rem" }}>
                  <iframe
                    src={`https://drive.google.com/file/d/${details?.id}/preview`}
                    height="400"
                    allow="autoplay"
                    style={{ border: "none", width: "100%" }}
                    title="edigital Video"
                  ></iframe>
                </Card>
              )}
              <SpecialNotes
                notes={selectedOrder?.comment || "No comments found !"}
              />
              {/* Order Information in Table */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}
              >
                Order Information
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Tracking ID</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.tracking_id}</TableCell>
                  </TableRow>
                  {decoded.role === "admin" && (
                    <TableRow>
                      <TableCell>
                        <strong>Order Amount</strong>
                      </TableCell>
                      <TableCell>{selectedOrder.order_amount}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.status}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Payment Status</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.payment_status}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Order Information in Table */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}
              >
                Bride Information
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.bride_name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Mother Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.bride_mother}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Father Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.bride_father}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Order Information in Table */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}
              >
                Groom Information
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.groom_name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Mother Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.groom_mother}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Father Name</strong>
                    </TableCell>
                    <TableCell>{selectedOrder.groom_father}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider style={{ margin: "1.5rem 0" }} />

              {/* Event Details */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: "bold",
                  marginBottom: "1rem",
                  marginTop: "1rem",
                }}
              >
                Event Details
              </Typography>

              <Grid container spacing={4}>
                {events.map((event) => (
                  <Grid key={event.id} item xl={6}>
                    <EventCard event={event} />
                  </Grid>
                ))}
              </Grid>
              <Divider style={{ margin: "1rem 0" }} />

              {/* Status and Payment Status Radio Buttons */}
              {decoded.role === "admin" && (
                <FormControl component="fieldset" style={{ marginTop: "1rem" }}>
                  <FormLabel component="legend">Status</FormLabel>
                  <RadioGroup
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    row
                  >
                    <FormControlLabel
                      value="Accepted"
                      control={<Radio />}
                      label="Accepted"
                    />
                    <FormControlLabel
                      value="Rejected"
                      control={<Radio />}
                      label="Rejected"
                    />
                    <FormControlLabel
                      value="In Progress"
                      control={<Radio />}
                      label="In Progress"
                    />
                    <FormControlLabel
                      value="Completed"
                      control={<Radio />}
                      label="Completed"
                    />
                  </RadioGroup>
                </FormControl>
              )}

              {decoded.role === "admin" && (
                <FormControl
                  component="fieldset"
                  disabled={status === "Rejected"}
                  style={{ marginTop: "1rem" }}
                >
                  <FormLabel component="legend">Payment Status</FormLabel>
                  <RadioGroup
                    value={paymentStatus}
                    onChange={(e) => {
                      setPaymentStatus(e.target.value);
                    }}
                    row
                  >
                    <FormControlLabel
                      value="Advance Paid"
                      control={<Radio />}
                      label="Advance Paid"
                    />
                    <FormControlLabel
                      value="Pending"
                      control={<Radio />}
                      label="Pending"
                    />
                    <FormControlLabel
                      value="Completed"
                      control={<Radio />}
                      label="Completed"
                    />
                    <FormControlLabel
                      value="Rejected"
                      disabled={status !== "Rejected"}
                      control={<Radio />}
                      label="Rejected"
                    />
                  </RadioGroup>
                </FormControl>
              )}
              <br />
              {decoded.role === "admin" && (
                <FormLabel>Amount Details</FormLabel>
              )}
              <br />
              {decoded.role === "admin" && (
                <TextField
                  style={{ marginTop: 12, minWidth: 300 }}
                  name="order_amonunt"
                  placeholder={`Enter Order Amount`}
                  type="number"
                  value={amount || selectedOrder?.order_amount}
                  label="Order Amount (advance amount includes)"
                  onChange={(e) => setAmount(e.target.value)}
                />
              )}
              {decoded.role === "admin" && (
                <TextField
                  style={{ marginTop: 12, minWidth: 300, marginLeft: 16 }}
                  name="advance_amonunt"
                  placeholder={`Enter Advance Amount`}
                  disabled={status === "Rejected"}
                  type="number"
                  label="Advance Amount"
                  value={advAmount || selectedOrder?.amount_received}
                  onChange={(e) => setAdvAmount(e.target.value)}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions style={{ padding: "1.5rem", justifyContent: "flex-end" }}>
        {decoded.role === "admin" && (
          <Button
            disabled={
              !(
                (paymentStatus === "Rejected" &&
                  status === "Rejected" &&
                  amount >= 0) ||
                (status === "Accepted" && amount > 0 && advAmount >= 0) ||
                (status === "In Progress" && amount > 0 && advAmount >= 0) ||
                (status === "Completed" && amount > 0 && advAmount >= 0)
              )
            }
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
        )}
        {decoded.role !== "admin" && (
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsDialog;
